import { useState, useCallback, useRef, useEffect } from 'react'
import type { UUID } from 'crypto'
import {
  Box,
  Typography,
  TextField,
  Alert,
  InputAdornment,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { STEPS } from '../onboardingSteps'
import { useOnboardingV2 } from '../OnboardingContext'
import { useWizardData } from '../hooks/useWizardData'
import { useStep } from '../hooks/useStep'
import { useToken } from '../hooks/useToken'
import {
  useCreateFundingNeedMutation,
  useUpdateFundingNeedMutation,
} from '@/utils/hooks/fundingNeeds'
import { useRegions, useRegionSets } from '@/utils/hooks/regions'
import { useCurrentUserOrgs } from '@/utils/hooks/orgs'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import {
  stepHeadingSx,
  stepDescriptionSx,
  formLabelSx,
  textFieldSx,
  inputSx,
  warningBoxSx,
} from '../styles'
import RegionAutocomplete from '../components/RegionAutocomplete'
import { COLORS } from '../theme'
import type { RegionData } from '@/models/Regions'
import type { DescriptionParts, WriteModeDraft } from '../types'
import SpeechToText from '@/components/SpeechToText'

// ── Description part questions (from main app) ──────────────────

const DESCRIPTION_QUESTIONS: ReadonlyArray<{
  key: keyof DescriptionParts
  question: string
  header: string
  placeholder: string
  optional?: boolean
}> = [
  {
    key: 'partOne',
    question: 'What is this funding for?',
    header: 'What this funding is for',
    placeholder: 'Describe what this funding will be used for',
  },
  {
    key: 'partTwo',
    question: 'What problem does it solve or need does it address?',
    header: 'What problem this funding addresses',
    placeholder: 'Explain the problem or need this funding addresses',
  },
  {
    key: 'partThree',
    question:
      'What impact or change will this create for your community/kaupapa or on your organisation?',
    header: 'What impact this funding will have',
    placeholder: 'Describe the impact or change this will create',
  },
  {
    key: 'partFour',
    question: 'Who benefits from this work/mahi?',
    header: 'Who will benefit from this funding',
    placeholder: 'Describe who will benefit from this work',
  },
  {
    key: 'partFive',
    question: 'Anything else you think is important for funders to know',
    header: 'Other important information',
    placeholder: 'Add any additional information you think is important',
    optional: true,
  },
]

const EMPTY_PARTS: DescriptionParts = {
  partOne: '',
  partTwo: '',
  partThree: '',
  partFour: '',
  partFive: '',
}

function buildDescription(parts: DescriptionParts): string {
  const sections = DESCRIPTION_QUESTIONS.filter(
    (q) => !q.optional || parts[q.key].trim(),
  ).map((q) => `${q.header}:\n${parts[q.key].trim()}`)
  return sections.join('\n\n')
}

function parseDescriptionParts(description: string): DescriptionParts | null {
  const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const headers = DESCRIPTION_QUESTIONS.map((q) => escape(q.header + ':'))
  const pattern = new RegExp(
    `${headers[0]}\\s*(?<p1>[\\s\\S]*?)` +
      `${headers[1]}\\s*(?<p2>[\\s\\S]*?)` +
      `${headers[2]}\\s*(?<p3>[\\s\\S]*?)` +
      `${headers[3]}\\s*(?<p4>[\\s\\S]*?)` +
      `(?:${headers[4]}\\s*(?<p5>[\\s\\S]*))?$`,
  )
  const match = pattern.exec(description)
  if (!match?.groups) return null
  return {
    partOne: match.groups.p1.trim(),
    partTwo: match.groups.p2.trim(),
    partThree: match.groups.p3.trim(),
    partFour: match.groups.p4.trim(),
    partFive: (match.groups.p5 ?? '').trim(),
  }
}

export default function FundingDetails() {
  const {
    orgUuid,
    fundingNeedId,
    maxStepReached,
    setStaleFrom,
    setFundingNeedId,
    setScanJobId,
    setApplicationId,
    setScanResultId,
  } = useOnboardingV2()

  return (
    <WriteMode
      orgUuid={orgUuid}
      fundingNeedId={fundingNeedId}
      maxStepReached={maxStepReached}
      setStaleFrom={setStaleFrom}
      setFundingNeedId={setFundingNeedId}
      setScanJobId={setScanJobId}
      setApplicationId={setApplicationId}
      setScanResultId={setScanResultId}
    />
  )
}

// ── Write mode sub-component ──────────────────────────────────────

function WriteMode({
  orgUuid,
  fundingNeedId: existingNeedId,
  maxStepReached,
  setStaleFrom,
  setFundingNeedId,
  setScanJobId,
  setApplicationId,
  setScanResultId,
}: {
  orgUuid: string | null
  fundingNeedId: number | null
  maxStepReached: number
  setStaleFrom: (
    v: number | null | ((prev: number | null) => number | null),
  ) => void
  setFundingNeedId: (id: number | null) => void
  setScanJobId: (id: string | null) => void
  setApplicationId: (id: string | null) => void
  setScanResultId: (id: string | null) => void
}) {
  const [wizardData, setWizardData] = useWizardData()
  const { getToken } = useToken()
  const { user, token: ctxToken, orgs } = getCurrentUser()
  const token = ctxToken ?? ''

  const createMutation = useCreateFundingNeedMutation()
  const updateMutation = useUpdateFundingNeedMutation()
  const { regions, isLoading: regionsLoading } = useRegions(token)
  const { regionSets } = useRegionSets(token)

  // Org data for "Use Organisation Locations" toggle
  const { orgData } = useCurrentUserOrgs(
    token,
    user ?? null,
    (orgs ?? []) as UUID[],
  )
  const org = orgData?.find((o) => o.uuid?.toString() === orgUuid)
  const orgRegionIds = org?.region_ids ?? []

  // Read directly from wizardData.writeModeDraft
  // Backward compat: if old draft has description but no descriptionParts, parse it
  const raw = wizardData.writeModeDraft
  const draft: WriteModeDraft = raw ?? {
    title: '',
    description: '',
    descriptionParts: EMPTY_PARTS,
    min_amount: '',
    amount: '',
    regionIds: [],
  }

  // Migrate old drafts that lack descriptionParts
  const parts =
    draft.descriptionParts ??
    (draft.description
      ? (parseDescriptionParts(draft.description) ?? {
          ...EMPTY_PARTS,
          partOne: draft.description,
        })
      : EMPTY_PARTS)

  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState({
    title: '',
    min_amount: '',
    amount: '',
    partOne: '',
    partTwo: '',
    partThree: '',
    partFour: '',
  })
  const initialDraftRef = useRef(JSON.stringify(draft))
  const shouldFocusFirstErrorRef = useRef(false)

  // "Use Organisation Locations" toggle — defaults to true for new drafts
  const usesOrgRegions = draft.uses_org_regions !== false

  // Refs for scroll-to-first-error
  const titleRef = useRef<HTMLDivElement>(null)
  const minAmountRef = useRef<HTMLDivElement>(null)
  const amountRef = useRef<HTMLDivElement>(null)
  const partOneRef = useRef<HTMLDivElement>(null)
  const partTwoRef = useRef<HTMLDivElement>(null)
  const partThreeRef = useRef<HTMLDivElement>(null)
  const partFourRef = useRef<HTMLDivElement>(null)

  const partErrorRefs: Partial<
    Record<keyof DescriptionParts, React.RefObject<HTMLDivElement | null>>
  > = {
    partOne: partOneRef,
    partTwo: partTwoRef,
    partThree: partThreeRef,
    partFour: partFourRef,
  }

  useEffect(() => {
    if (!shouldFocusFirstErrorRef.current) return
    shouldFocusFirstErrorRef.current = false
    const ordered: Array<{
      key: keyof typeof fieldErrors
      ref: React.RefObject<HTMLDivElement | null>
    }> = [
      { key: 'title', ref: titleRef },
      { key: 'min_amount', ref: minAmountRef },
      { key: 'amount', ref: amountRef },
      { key: 'partOne', ref: partOneRef },
      { key: 'partTwo', ref: partTwoRef },
      { key: 'partThree', ref: partThreeRef },
      { key: 'partFour', ref: partFourRef },
    ]
    for (const { key, ref } of ordered) {
      if (fieldErrors[key] && ref.current) {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        ref.current.querySelector<HTMLElement>('input, textarea')?.focus()
        break
      }
    }
  }, [fieldErrors])

  // Derive selected regions from draft IDs + loaded region list
  const selectedRegions = (regions ?? []).filter((r: RegionData) =>
    draft.regionIds.includes(r.id),
  )

  const updateDraft = useCallback(
    (updates: Partial<WriteModeDraft> | ((current: WriteModeDraft) => Partial<WriteModeDraft>)) => {
      setWizardData((prev) => {
        const current = prev.writeModeDraft ?? draft
        const resolved = typeof updates === 'function' ? updates(current) : updates
        return { writeModeDraft: { ...current, ...resolved } }
      })
    },
    [setWizardData, draft],
  )

  const updatePart = useCallback(
    (key: keyof DescriptionParts, value: string | ((current: string) => string)) => {
      updateDraft((current) => {
        const currentParts = current.descriptionParts ?? EMPTY_PARTS
        const resolved = typeof value === 'function' ? value(currentParts[key] ?? '') : value
        const newParts = { ...currentParts, [key]: resolved }
        return { descriptionParts: newParts, description: buildDescription(newParts) }
      })
      if (key in fieldErrors) {
        setFieldErrors((prev) => ({ ...prev, [key]: '' }))
      }
    },
    [updateDraft, fieldErrors],
  )

  const beforeNext = useCallback(async () => {
    setError(null)

    const normalizeAmount = (val: string) => val.replace(/[$,\s]/g, '')

    // Validate required fields
    const normalizedAmount = normalizeAmount(draft.amount)
    const normalizedMinAmount = draft.min_amount
      ? normalizeAmount(draft.min_amount)
      : ''

    let amountError = ''
    if (!draft.amount.trim()) {
      amountError = 'Please enter the full amount needed'
    } else if (isNaN(Number(normalizedAmount)) || Number(normalizedAmount) <= 0) {
      amountError = 'Please enter a valid positive number'
    }

    let minAmountError = ''
    if (normalizedMinAmount) {
      if (isNaN(Number(normalizedMinAmount)) || Number(normalizedMinAmount) <= 0) {
        minAmountError = 'Please enter a valid positive number'
      } else if (
        !amountError &&
        Number(normalizedMinAmount) > Number(normalizedAmount)
      ) {
        minAmountError = 'Minimum amount cannot be greater than the full amount'
      }
    }

    const errors = {
      title: !draft.title.trim() ? 'Please enter a title' : '',
      min_amount: minAmountError,
      amount: amountError,
      partOne: !parts.partOne.trim() ? 'Please answer this question' : '',
      partTwo: !parts.partTwo.trim() ? 'Please answer this question' : '',
      partThree: !parts.partThree.trim() ? 'Please answer this question' : '',
      partFour: !parts.partFour.trim() ? 'Please answer this question' : '',
    }
    if (Object.values(errors).some(Boolean)) {
      shouldFocusFirstErrorRef.current = true
      setFieldErrors(errors)
      return false
    }

    const submittableAmount = normalizedAmount
    const submittableMinAmount = normalizedMinAmount || undefined

    try {
      const t = await getToken()
      const description = buildDescription(parts)
      const regionIds = usesOrgRegions ? [] : draft.regionIds

      // Update existing funding need instead of creating a duplicate
      if (existingNeedId) {
        await updateMutation.mutateAsync({
          token: t,
          fundingNeedId: existingNeedId,
          updates: {
            title: draft.title.trim(),
            description,
            min_amount: submittableMinAmount,
            amount: submittableAmount,
            region_ids: regionIds,
            timing_criteria: undefined,
            force: true,
            ignore_assessment: true,
          },
        })
        // Only mark stale + clear downstream if content actually changed
        const draftChanged = JSON.stringify(draft) !== initialDraftRef.current
        if (draftChanged) {
          setScanJobId(null)
          setApplicationId(null)
          setScanResultId(null)
          if (maxStepReached >= STEPS.SCAN_RUNNING) {
            setStaleFrom((prev) =>
              prev === null
                ? STEPS.SCAN_RUNNING
                : Math.min(prev, STEPS.SCAN_RUNNING),
            )
          }
        }
        return
      }

      // Create new funding need (first visit)
      const result = await createMutation.mutateAsync({
        token: t,
        fundingNeed: {
          org_uuid: orgUuid as UUID,
          title: draft.title.trim(),
          description,
          min_amount: submittableMinAmount,
          amount: submittableAmount,
          region_ids: regionIds,
          timing_criteria: null,
          force: true,
          ignore_assessment: true,
          funding_ideas_session_id: draft.fundingIdeasSessionId ?? undefined,
        },
      })

      if ('id' in result) {
        setFundingNeedId(result.id)
        setScanJobId(null)
        setApplicationId(null)
        setScanResultId(null)
        if (maxStepReached >= STEPS.SCAN_RUNNING) {
          setStaleFrom((prev) =>
            prev === null
              ? STEPS.SCAN_RUNNING
              : Math.min(prev, STEPS.SCAN_RUNNING),
          )
        }
      }
    } catch {
      setError(
        'Something went wrong saving your funding details. Please try again.',
      )
      return false
    }
  }, [
    getToken,
    orgUuid,
    existingNeedId,
    draft,
    parts,
    usesOrgRegions,
    createMutation,
    updateMutation,
    setFundingNeedId,
    setScanJobId,
    setApplicationId,
    setScanResultId,
    maxStepReached,
    setStaleFrom,
  ])

  useStep({ beforeNext })

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Typography variant="h5" sx={stepHeadingSx}>
        Describe your funding need
      </Typography>
      <Typography sx={stepDescriptionSx}>
        Answer the questions below to help funders understand what you need and
        why it matters.
      </Typography>

      {wizardData.fundingMode === 'guided' && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            ...warningBoxSx,
          }}
        >
          <InfoOutlinedIcon
            sx={{
              color: COLORS.warningIcon,
              fontSize: 22,
              mt: 0.25,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="body2"
            sx={{ color: COLORS.labelText, lineHeight: 1.6 }}
          >
            This is a first draft of a funding need based on the idea you
            selected. Review and adapt the content to ensure it fits your
            kaupapa and what you&apos;d like to seek funding for.
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Title */}
      <Box ref={titleRef}>
        <Typography sx={formLabelSx}>
          Title{' '}
          <Box component="span" aria-hidden="true">
            *
          </Box>
        </Typography>
        <Typography variant="body2" sx={{ color: COLORS.secondary, mb: 1 }}>
          A name to describe what this request is for
        </Typography>
        <TextField
          fullWidth
          value={draft.title}
          onChange={(e) => {
            updateDraft({ title: e.target.value })
            setFieldErrors((prev) => ({ ...prev, title: '' }))
          }}
          placeholder="Enter a title for your funding request"
          error={!!fieldErrors.title}
          helperText={fieldErrors.title || undefined}
          sx={textFieldSx}
          slotProps={{ input: { sx: inputSx } }}
        />
      </Box>

      {/* Amount */}
      <Box>
        <Typography sx={formLabelSx}>Amount Needed</Typography>
        <Typography variant="body2" sx={{ color: COLORS.secondary, mb: 1 }}>
          You can include dollar signs ($) and commas (e.g., $1,000)
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 3,
          }}
        >
          <Box ref={minAmountRef} sx={{ flex: 1 }}>
            <Typography
              sx={{
                ...formLabelSx,
                fontWeight: 500,
                fontSize: '0.875rem',
                color: COLORS.secondary,
              }}
            >
              Minimum Amount (optional)
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: COLORS.secondary, mb: 1, display: 'block' }}
            >
              The minimum contribution amount you would accept
            </Typography>
            <TextField
              fullWidth
              type="text"
              value={draft.min_amount ?? ''}
              error={!!fieldErrors.min_amount}
              helperText={fieldErrors.min_amount || undefined}
              onChange={(e) => {
                updateDraft({ min_amount: e.target.value })
                setFieldErrors((prev) => ({ ...prev, min_amount: '' }))
              }}
              sx={textFieldSx}
              slotProps={{
                input: {
                  sx: inputSx,
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                },
              }}
            />
          </Box>
          <Box ref={amountRef} sx={{ flex: 1 }}>
            <Typography
              sx={{
                ...formLabelSx,
                fontWeight: 500,
                fontSize: '0.875rem',
                color: COLORS.secondary,
              }}
            >
              Full Amount Needed{' '}
              <Box component="span" aria-hidden="true">
                *
              </Box>
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: COLORS.secondary, mb: 1, display: 'block' }}
            >
              The full amount of funding you need
            </Typography>
            <TextField
              fullWidth
              type="text"
              value={draft.amount}
              onChange={(e) => {
                updateDraft({ amount: e.target.value })
                setFieldErrors((prev) => ({ ...prev, amount: '' }))
              }}
              error={!!fieldErrors.amount}
              helperText={fieldErrors.amount || undefined}
              sx={textFieldSx}
              slotProps={{
                input: {
                  sx: inputSx,
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Regions */}
      <Box>
        <Typography sx={formLabelSx}>Locations</Typography>
        <Typography variant="body2" sx={{ color: COLORS.secondary, mb: 1 }}>
          Select the location(s) where the funding will be used.
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              checked={usesOrgRegions}
              onChange={(e) => {
                updateDraft({
                  uses_org_regions: e.target.checked,
                  regionIds: [],
                })
              }}
              sx={{ '&.MuiCheckbox-colorPrimary': { color: COLORS.primary } }}
            />
          }
          label="Use Organisation Locations"
          sx={{ mb: 1 }}
        />

        <Typography
          variant="body2"
          fontStyle="italic"
          ml={2}
          my={1}
          component="div"
          sx={{ color: COLORS.labelText }}
        >
          Organisation Locations:
          {orgRegionIds.length > 0 ? (
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              {orgRegionIds
                .map((id: number) =>
                  (regions ?? []).find((r: RegionData) => r.id === id),
                )
                .filter(Boolean)
                .map((region: RegionData) => {
                  const hierarchy = [
                    region.country,
                    region.region,
                    region.council,
                    region.major,
                    region.suburb,
                  ]
                    .filter(Boolean)
                    .join(' \u2192 ')
                  return (
                    <li key={region.id} style={{ marginBottom: '4px' }}>
                      <strong>{region.name}</strong>
                      {hierarchy && (
                        <span style={{ color: 'gray', marginLeft: '8px' }}>
                          ({hierarchy})
                        </span>
                      )}
                    </li>
                  )
                })}
            </ul>
          ) : (
            <span> None</span>
          )}
        </Typography>

        <Typography variant="body2" sx={{ color: COLORS.secondary, my: 1 }}>
          If you have already stated all the locations in your organisation
          profile you can use the same locations.
        </Typography>
        <Typography variant="body2" sx={{ color: COLORS.secondary, my: 1 }}>
          Untick this option to specify different locations for this funding
          need only. When unticked, only the locations you select here will be
          used (not your organisation locations).
        </Typography>

        {!usesOrgRegions && (
          <RegionAutocomplete
            regions={regions ?? []}
            locationSets={regionSets ?? []}
            value={selectedRegions}
            onChange={(regs: RegionData[]) =>
              updateDraft({ regionIds: regs.map((r) => r.id) })
            }
            onRegionSetSelect={(regionIds) => {
              const currentIds = draft.regionIds
              const newIds = [...new Set([...currentIds, ...regionIds])]
              updateDraft({ regionIds: newIds })
            }}
            loading={regionsLoading}
          />
        )}
      </Box>

      {/* Description */}
      <Box>
        <Typography sx={formLabelSx}>Description</Typography>
        <Typography variant="body2" sx={{ color: COLORS.secondary, mb: 1 }}>
          What is the funding for? Why does it matter? What impact will this
          have on your community/kaupapa or on your organisation? Who benefits
          from this service, and anything else you think is important to add.
        </Typography>
      </Box>

      {DESCRIPTION_QUESTIONS.map((q, i) => {
        const isRequired = !q.optional
        const errorKey = q.key as keyof typeof fieldErrors
        const hasError = isRequired && !!fieldErrors[errorKey]
        const ref = partErrorRefs[q.key] ?? null
        return (
          <Box key={q.key} ref={ref}>
            <Typography sx={{ ...formLabelSx, mt: i === 0 ? 0 : 0.5 }}>
              {q.question}
              {q.optional ? (
                <Typography
                  component="span"
                  sx={{
                    color: COLORS.secondary,
                    fontWeight: 400,
                    ml: 0.5,
                    fontSize: '0.85rem',
                  }}
                >
                  (Optional)
                </Typography>
              ) : (
                <Box component="span" aria-hidden="true">
                  {' '}
                  *
                </Box>
              )}
            </Typography>
            {token && (
              <SpeechToText
                token={token}
                onTranscription={(text) =>
                  updatePart(q.key, (current) => current ? `${current}\n\n${text}` : text)
                }
              />
            )}
            <TextField
              fullWidth
              multiline
              minRows={4}
              maxRows={8}
              value={parts[q.key]}
              onChange={(e) => updatePart(q.key, e.target.value)}
              placeholder={q.placeholder}
              error={hasError}
              helperText={hasError ? fieldErrors[errorKey] : undefined}
              sx={textFieldSx}
              slotProps={{ input: { sx: inputSx } }}
            />
          </Box>
        )
      })}
    </Box>
  )
}
